import React from 'react';
import { Box, Stack, Typography, Skeleton } from '@compass/core';
import { StudentDataProvider, useStudentData } from '../contexts/StudentDataContext';
import { DashboardCard } from "../components/DashboardCard";
import { StudentFilterControls } from './StudentFilterControls';
import { openFile } from "@compass/core";
import ApiService from 'services/apiService';
import { StudentCountsByGradeRequest, StudentGroupBy } from 'generated/openApi';

const groupByLabels: Record<StudentGroupBy, { column: string; footer: string }> = {
    [StudentGroupBy.Grade]: { column: 'Grade', footer: 'Grade Levels Served' },
    [StudentGroupBy.School]: { column: 'School', footer: 'Schools Served' },
    [StudentGroupBy.Year]: { column: 'Year', footer: 'School Years' },
};

const countColWidth = 90;
const percentColWidth = 80;

const TotalStudentsDisplay = () => {
    const { data, loading, error } = useStudentData();

    if (loading) {
        return <Skeleton variant="rounded" height={300} />;
    }

    if (error) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!data || data.rows.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>
                    {data?.schoolYearDisplay
                        ? `No enrollments found for ${data.schoolYearDisplay}`
                        : 'No enrollment data available'}
                </Typography>
            </Box>
        );
    }

    const labels = groupByLabels[data.groupBy];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack
                direction="row"
                alignItems="center"
                sx={{ mb: 1, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}
            >
                <Typography variant="caption" fontWeight="bold" sx={{ flex: 1, textTransform: 'uppercase', color: 'text.secondary' }}>
                    {labels.column}
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ minWidth: countColWidth, textAlign: 'right', textTransform: 'uppercase', color: 'text.secondary' }}>
                    {data.schoolYearDisplay}
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ minWidth: percentColWidth, textAlign: 'right', textTransform: 'uppercase', color: 'text.secondary' }}>
                    % of Total
                </Typography>
            </Stack>

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Stack direction="column">
                    {data.rows.map((row) => (
                        <Stack
                            key={row.groupId}
                            direction="row"
                            alignItems="center"
                            sx={{
                                py: 1,
                                px: 0.5,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            <Typography sx={{ flex: 1, color: 'primary.main', fontWeight: 500 }}>
                                {row.groupName}
                            </Typography>
                            <Typography sx={{ minWidth: countColWidth, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                {row.studentCount.toLocaleString()}
                            </Typography>
                            <Typography sx={{ minWidth: percentColWidth, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'text.secondary', fontSize: '0.9rem' }}>
                                {`${row.percentage.toFixed(1)}%`}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>

            <Stack
                direction="row"
                alignItems="center"
                sx={{
                    py: 1.5,
                    px: 1,
                    mt: 1,
                    borderTop: '2px solid',
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(49, 130, 189, 0.08)',
                    borderRadius: '4px'
                }}
            >
                <Typography sx={{ flex: 1, fontWeight: 'bold' }}>
                    Unduplicated Total
                </Typography>
                <Typography sx={{ minWidth: countColWidth, textAlign: 'right', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {data.total.toLocaleString()}
                </Typography>
                <Typography sx={{ minWidth: percentColWidth, textAlign: 'right', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    100.0%
                </Typography>
            </Stack>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {labels.footer}
                </Typography>
            </Box>
        </Box>
    );
};

const TotalStudentsCardContent = () => {
    const { data, filter } = useStudentData();

    const handleExport = async () => {
        const request: StudentCountsByGradeRequest = {
            schoolYear: filter.groupBy !== StudentGroupBy.Year ? filter.schoolYear : undefined,
            programId: filter.programId,
            groupBy: filter.groupBy,
            schoolYears: filter.groupBy === StudentGroupBy.Year && filter.schoolYears.length > 0
                ? filter.schoolYears
                : undefined,
            schoolId: filter.schoolId,
            gradeLevelId: filter.gradeLevelId,
        };

        const response = await ApiService.home.exportStudentCountsByGrade(request);
        openFile(response.fileName, response.base64String, response.contentType);
    };

    return (
        <DashboardCard title="Enrollments" onExport={handleExport}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <StudentFilterControls />
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                    <TotalStudentsDisplay />
                </Box>
            </Box>
        </DashboardCard>
    );
};

export const TotalStudentsCard = () => {
    return (
        <StudentDataProvider>
            <TotalStudentsCardContent />
        </StudentDataProvider>
    );
};